import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { Cell, getStatusColor, HeaderCell, HeaderRow, ModalText, Row, Status, Table, TableBody, TableHead } from 'components';
import { theme, toRem } from 'design';
import { centsToDollarsDisplay } from 'utils/currency';
import { formatDateAbbreviatedWithDay } from 'utils/date';
import { capitalize } from 'utils/strings';
import { IReviewDay } from './utils';

const LOADING_ROW_COUNT = 3;

// A monthly period with several payment sets a day runs to dozens of rows, so the table takes the modal's remaining
// height and scrolls under a pinned header.
const ScrollableTable = styled(Table)`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: ${theme.color.neutral[100]};
  }

  /* Nothing here is clickable, so rows keep their color under the pointer. */
  tbody tr:hover {
    background-color: ${theme.color.white};
  }
`;

const BusinessDay = styled.strong`
  display: block;
  font-size: ${toRem(theme.font.size.lg)};
`;

const SetId = styled.span`
  display: block;
  margin-top: ${toRem(theme.space.xs)};
  font-size: ${toRem(theme.font.size.xs)};
  color: ${theme.color.neutral[500]};
`;

const NetPaid = styled.strong`
  font-size: ${toRem(theme.font.size.md)};
`;

const Statuses = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.xs)};
`;

// One row per payment set, with room for its status stack; the divider closes the day, not each set.
const SetRow = styled(Row)<{ $continues?: boolean }>`
  td {
    padding-top: ${toRem(theme.space.lg)};
    padding-bottom: ${toRem(theme.space.lg)};
  }

  ${({ $continues }) =>
    $continues &&
    css`
      td {
        border-bottom: none;
      }
    `}
`;

const EmptyDay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: ${toRem(theme.space.sm)};
  border-radius: ${toRem(theme.space.xs)};
  background-color: ${theme.color.neutral[100]};
  font-size: ${toRem(theme.font.size.xs)};
  color: ${theme.color.neutral[900]};
`;

interface IReviewStepProps {
  days: IReviewDay[];
  isLoading?: boolean;
  dataCy?: string;
}

// Step 1: every business day in the period with its payment sets.
const ReviewStep = ({ days, isLoading = false, dataCy = 'close-pay-period-review' }: IReviewStepProps) => (
  <>
    <ModalText marginBottom={theme.space.xl}>Please review each day and ensure all payments have been entered before closing the pay period.</ModalText>
    <ScrollableTable data-cy={dataCy}>
      <TableHead>
        <HeaderRow>
          <HeaderCell>Business Day</HeaderCell>
          <HeaderCell>Net Paid</HeaderCell>
          <HeaderCell>Status</HeaderCell>
          <HeaderCell>Sent By</HeaderCell>
        </HeaderRow>
      </TableHead>
      <TableBody>
        {isLoading
          ? Array.from({ length: LOADING_ROW_COUNT }, (_, index) => (
              <Row key={index} data-cy="loading-row">
                {[120, 60, 80, 100].map((width) => (
                  <Cell key={width}>
                    <Skeleton width={width} />
                  </Cell>
                ))}
              </Row>
            ))
          : days.map((day) => {
              if (day.paymentSets.length === 0) {
                return (
                  <Row key={day.date} data-cy="review-day-row" data-value={day.date}>
                    <Cell>
                      <BusinessDay>{formatDateAbbreviatedWithDay(day.date)}</BusinessDay>
                    </Cell>
                    <Cell colSpan={3}>
                      <EmptyDay data-cy="review-day-note">{day.isUpcoming ? 'Upcoming' : 'No payments'}</EmptyDay>
                    </Cell>
                  </Row>
                );
              }
              return day.paymentSets.map((paymentSet, index) => (
                <SetRow key={paymentSet.id} $continues={index < day.paymentSets.length - 1} data-cy="review-day-row" data-value={day.date}>
                  <Cell>
                    {index === 0 && <BusinessDay>{formatDateAbbreviatedWithDay(day.date)}</BusinessDay>}
                    <SetId>{paymentSet.id}</SetId>
                  </Cell>
                  <Cell>
                    <NetPaid>{centsToDollarsDisplay(paymentSet.net)}</NetPaid>
                  </Cell>
                  <Cell>
                    <Statuses>
                      {paymentSet.statuses.map(({ status, count }) => (
                        <Status key={status} appearance={getStatusColor(status)} label={`${capitalize(status)} (${count})`} />
                      ))}
                    </Statuses>
                  </Cell>
                  <Cell>{paymentSet.sentBy}</Cell>
                </SetRow>
              ));
            })}
      </TableBody>
    </ScrollableTable>
  </>
);

export default ReviewStep;
